import { FC, useCallback } from "react";
import { Modal } from "@shopify/polaris";
import moment from "moment";

import { closeModal } from "../../../store/features/modal.js";
import { OPEN_MODAL_NOTIFICATION_PREVIEW } from "../../../constants/modalTypes.js";
import { useAppDispatch, useAppSelector } from "../../../hooks/redux.js";
import summaryTemplate from "../../../../helpers/sendNotify/templates/SummaryTemplate.js";
import notificationTemplate from "../../../../helpers/sendNotify/templates/NotificationTemplate.js";
import { snowboard_1_image, snowboard_2_image, snowboard_3_image } from "../../../assets/index.js";
import {
  TIME_12_HR,
  TIME_1_DAY,
  TIME_7_DAY,
  TIME_30_DAY,
} from "../../../../constants/timeInMs.js";

const LOGO_HEADER = "https://stockhide.s3.amazonaws.com/reports-assets/logo-header.png";
const LOGO_FOOTER = "https://stockhide.s3.amazonaws.com/reports-assets/logo-footer.png";
const NOTIFICATION_ICON = "https://stockhide.s3.amazonaws.com/reports-assets/notification-icon.png";

interface NotificationPreviewModalProps {
  actionType?: "ACTION_TYPE_STOCK" | "ACTION_TYPE_ATTRIBUTES";
}

const frequencyParse = {
  TIME_12_HR: "12 hours",
  TIME_1_DAY: "Daily",
  TIME_7_DAY: "Weekly",
  TIME_30_DAY: "Monthly",
};

const subject = {
  outStockNotify: "Out Of Stock",
  lowStockNotify: "Low On Stock",
  backStockNotify: "Back In Stock"
};

const mockProducts = [{
  title: "Snowboard purple",
  attributes: ["stock levels"],
  action: "Hidden",
  stock: 100,
  updatedAt: moment(new Date()).format("DD.MM.YYYY  HH:mm"),
  url: "#",
  image: snowboard_1_image,
  stockType: "OUT_STOCK",
}, {
  title: "Snowboard sky",
  attributes: ["stock levels"],
  action: "Pushed Down",
  stock: 150,
  updatedAt: moment(new Date()).format("DD.MM.YYYY  HH:mm"),
  url: "#",
  image: snowboard_2_image,
  stockType: "LOW_STOCK",
}, {
  title: "Snowboard yellow",
  attributes: ["stock levels"],
  action: "Made visible",
  stock: 325,
  updatedAt: moment(new Date()).format("DD.MM.YYYY  HH:mm"),
  url: "#",
  image: snowboard_3_image,
  stockType: "BACK_STOCK",
}]

const NotificationPreviewModal: FC<NotificationPreviewModalProps> = ({ actionType = "ACTION_TYPE_STOCK" }) => {
  const { isOpen, modalType, modalData } = useAppSelector((state) => state.modal);
  const dispatch = useAppDispatch();
  const shop = useAppSelector((state) => state.shop.data);

  const handleClose = useCallback(() => {
    dispatch(closeModal());
  }, []);
  
  let htmlTemplate: string;
  if (modalData?.frequency === TIME_12_HR) {
    htmlTemplate = summaryTemplate({
      shop: shop?.myshopifyDomain,
      events: mockProducts,
      summaryPdfLink: "#",
      summaryCsvLink: "#",
      summaryXlsxLink: "#",
      outOfStockPdfLink: "#",
      lowOnStockPdfLink: "#",
      backInStockPdfLink: "#",
      outOfStockCsvLink: "#",
      lowOnStockCsvLink: "#",
      backInStockCsvLink: "#",
      outOfStockXlsxLink: "#",
      lowOnStockXlsxLink: "#",
      backInStockXlsxLink: "#",
      logoHeader: LOGO_HEADER,
      logoFooter: LOGO_FOOTER,
      notificationIcon: NOTIFICATION_ICON,
      frequencyType: frequencyParse.TIME_12_HR,
      stockType: subject.outStockNotify,
      createdAt: moment().format("DD/MM/YYYY  HH:mm"),
    });
  } else if (modalData?.frequency === TIME_1_DAY) {
    htmlTemplate = summaryTemplate({
      shop: shop?.myshopifyDomain,
      events: mockProducts,
      summaryPdfLink: "#",
      summaryCsvLink: "#",
      summaryXlsxLink: "#",
      outOfStockPdfLink: "#",
      lowOnStockPdfLink: "#",
      backInStockPdfLink: "#",
      outOfStockCsvLink: "#",
      lowOnStockCsvLink: "#",
      backInStockCsvLink: "#",
      outOfStockXlsxLink: "#",
      lowOnStockXlsxLink: "#",
      backInStockXlsxLink: "#",
      logoHeader: LOGO_HEADER,
      logoFooter: LOGO_FOOTER,
      notificationIcon: NOTIFICATION_ICON,
      frequencyType: frequencyParse.TIME_1_DAY,
      stockType: subject.outStockNotify,
      createdAt: moment().format("DD/MM/YYYY  HH:mm"),
    });
  } else if (modalData?.frequency === TIME_7_DAY) {
    htmlTemplate = summaryTemplate({
      shop: shop?.myshopifyDomain,
      events: mockProducts,
      summaryPdfLink: "#",
      summaryCsvLink: "#",
      summaryXlsxLink: "#",
      outOfStockPdfLink: "#",
      lowOnStockPdfLink: "#",
      backInStockPdfLink: "#",
      outOfStockCsvLink: "#",
      lowOnStockCsvLink: "#",
      backInStockCsvLink: "#",
      outOfStockXlsxLink: "#",
      lowOnStockXlsxLink: "#",
      backInStockXlsxLink: "#",
      logoHeader: LOGO_HEADER,
      logoFooter: LOGO_FOOTER,
      notificationIcon: NOTIFICATION_ICON,
      frequencyType: frequencyParse.TIME_7_DAY,
      stockType: subject.lowStockNotify,
      createdAt: moment().format("DD/MM/YYYY  HH:mm"),
    });
  } else if (modalData?.frequency === TIME_30_DAY) {
    htmlTemplate = summaryTemplate({
      shop: shop?.myshopifyDomain,
      events: mockProducts,
      summaryPdfLink: "#",
      summaryCsvLink: "#",
      summaryXlsxLink: "#",
      outOfStockPdfLink: "#",
      lowOnStockPdfLink: "#",
      backInStockPdfLink: "#",
      outOfStockCsvLink: "#",
      lowOnStockCsvLink: "#",
      backInStockCsvLink: "#",
      outOfStockXlsxLink: "#",
      lowOnStockXlsxLink: "#",
      backInStockXlsxLink: "#",
      logoHeader: LOGO_HEADER,
      logoFooter: LOGO_FOOTER,
      notificationIcon: NOTIFICATION_ICON,
      frequencyType: frequencyParse.TIME_30_DAY,
      stockType: subject.backStockNotify,
      createdAt: moment().format("DD/MM/YYYY  HH:mm"),
    });
  } else {
    htmlTemplate = notificationTemplate({
      actionType,
      shop: shop?.myshopifyDomain,
      events: mockProducts,
      logoHeader: LOGO_HEADER,
      logoFooter: LOGO_FOOTER,
      notificationIcon: NOTIFICATION_ICON,
      stockType: "Product(s) status has <b>changed</b>",
      createdAt: moment().format("DD/MM/YYYY  HH:mm"),
    });
  }

  return (
    <Modal
      open={isOpen && modalType === OPEN_MODAL_NOTIFICATION_PREVIEW}
      onClose={handleClose}
      title="Notification example"
    >
      <Modal.Section>
        <div style={{ margin: "0 auto", overflow: "hidden" }}>
          <div dangerouslySetInnerHTML={{ __html: htmlTemplate }} />
        </div>
      </Modal.Section>
    </Modal>
  );
};

export default NotificationPreviewModal;